import clsx from 'clsx';
import { Chip, Grid, styled } from '@mui/material';

import { type FieldProps } from './components/BaseTextField';
import EditorHtml from './components/EditorHtml';
import HTMLRenderer from '../HTMLRenderer';

export type EditorHtmlFieldProps = Omit<
	FieldProps<string>,
	'startAdornment' | 'endAdornment' | 'isFormSubmitted' | 'isPristine' | 'isValid' | 'setValue'
>;

const Root = styled('div')(({ theme }) => ({
	'&.EditorHtmlField-root': {
		padding: 12,
		paddingTop: 10,
		paddingBottom: 8,
		borderBottom: `1px dotted ${
			theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.42)' : 'rgba(255, 255, 255, 0.7)'
		}`
	},
	'&.EditorHtmlField-root-edit': {
		borderBottom: `1px solid ${
			theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.42)' : 'rgba(255, 255, 255, 0.7)'
		}`,
		borderTopLeftRadius: 4,
		borderTopRightRadius: 4,
		backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.09)',
		'&:hover': {
			backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.09)' : 'rgba(255, 255, 255, 0.13)'
		}
	},
	'&.EditorHtmlField-root-logs': {
		borderBottom: 0,
		cursor: 'pointer'
	}
}));

export default function EditorHtmlField(props: EditorHtmlFieldProps) {
	const hasLogs = props.logProps?.hasLogs(props.name);

	function fieldParser(fieldValue) {
		return <HTMLRenderer value={fieldValue} />;
	}

	return (
		<Root
			className={clsx(
				'EditorHtmlField-root',
				(props.isEdit || hasLogs) && 'EditorHtmlField-root-edit',
				hasLogs && 'EditorHtmlField-root-logs',
				props.className
			)}
			onClick={() => hasLogs && props.logProps?.openLogsDialog(props.name, props.label, fieldParser)}
		>
			{!hasLogs ? (
				<EditorHtml
					name={props.name}
					label={props.label}
					value={props.value}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
				/>
			) : (
				<Grid container justifyContent="space-between" alignItems="center">
					<Grid size="grow">
						<HTMLRenderer value={props.value} />
					</Grid>
					<Grid>
						<Chip label={props.logProps?.logsCounters[props.name]} size="small" color="primary" />
					</Grid>
				</Grid>
			)}
		</Root>
	);
}
